import type { TyreOffer } from '../lib/tyreOffers'

/**
 * EU tyre label: fuel efficiency, wet grip and exterior noise.
 *
 * Grades are shown with their meaning rather than as a bare letter — "Wet grip
 * A" means nothing to most people, so each chip is labelled and given an
 * accessible name that spells it out. Colour alone never carries the grade:
 * the letter is always present, so it survives greyscale and colour blindness.
 *
 * Only renders what the supplier actually provided. A feed with no label data
 * produces nothing rather than a row of dashes.
 */

interface EuLabelProps {
  offer: TyreOffer
}

/** A is best; the scale was re-based to A–E in 2021 but older stock shows A–G. */
function gradeRank(grade: string): number {
  return grade.charCodeAt(0) - 'A'.charCodeAt(0)
}

function gradeTone(grade: string): 'good' | 'fair' | 'poor' {
  const rank = gradeRank(grade)
  if (rank <= 1) return 'good'
  if (rank <= 3) return 'fair'
  return 'poor'
}

/** Below 68 dB earns three waves on the label, 68–71 two, above that one. */
function noiseClass(db: number): number {
  if (db <= 67) return 3
  if (db <= 71) return 2
  return 1
}

export function EuLabel({ offer }: EuLabelProps) {
  const hasAnything =
    offer.fuelEfficiency != null || offer.wetGrip != null || offer.noiseDb != null

  if (!hasAnything) return null

  return (
    <div className="ac-eulabel">
      {offer.fuelEfficiency ? (
        <span
          className={`ac-eulabel__chip ac-eulabel__chip--${gradeTone(offer.fuelEfficiency)}`}
          title="EU label fuel efficiency"
        >
          <span className="ac-eulabel__grade" aria-hidden="true">
            {offer.fuelEfficiency}
          </span>
          <span className="ac-eulabel__caption" aria-hidden="true">
            Fuel
          </span>
          <span className="ac-sr-only">Fuel efficiency grade {offer.fuelEfficiency} of A to E</span>
        </span>
      ) : null}

      {offer.wetGrip ? (
        <span
          className={`ac-eulabel__chip ac-eulabel__chip--${gradeTone(offer.wetGrip)}`}
          title="EU label wet grip"
        >
          <span className="ac-eulabel__grade" aria-hidden="true">
            {offer.wetGrip}
          </span>
          <span className="ac-eulabel__caption" aria-hidden="true">
            Wet grip
          </span>
          <span className="ac-sr-only">Wet grip grade {offer.wetGrip} of A to E</span>
        </span>
      ) : null}

      {offer.noiseDb != null ? (
        <span className="ac-eulabel__chip ac-eulabel__chip--noise" title="EU label exterior noise">
          <span className="ac-eulabel__grade" aria-hidden="true">
            {Math.round(offer.noiseDb)}
          </span>
          <span className="ac-eulabel__caption" aria-hidden="true">
            dB
          </span>
          <span className="ac-sr-only">
            Exterior noise {Math.round(offer.noiseDb)} decibels, class{' '}
            {noiseClass(offer.noiseDb)} of 3
          </span>
        </span>
      ) : null}
    </div>
  )
}
